import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Checkbox, FormControlLabel, IconButton, Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Field, FieldLabel } from '../../UI';
import { LinkAdicionar } from '../abas/BlocosCadastrais';
import * as C from '../../../theme/colors.constants';

/**
 * Endereços da aba Devedor (AW2-1134 Etapa 3) — porta de `EnderecosV2/Enderecos.jsx`
 * real (`src/components/UI/EnderecosV2/`), lista real de múltiplos itens com
 * add/remove/"Principal" mutuamente exclusivo.
 *
 * Regras replicadas fielmente do componente real:
 * - Ícone de lixeira oculto no item `index === 0` (`showTrashAtFirstItemHandler`
 *   real — `isCorrigir` não existe nesta tela, então sempre "primeiro item
 *   nunca remove", sem exceção).
 * - Remover um endereço que era `principal` promove o novo item de índice 0
 *   (do array já sem o removido) a `principal: true` — nunca fica sem
 *   nenhum item principal quando a lista não está vazia (`removeEnderecoHandler`
 *   real).
 * - Marcar "Principal" desmarca todos os outros primeiro, depois marca só o
 *   clicado (`choosePrincipalHandler`/`enderecosWithPrincipalFalse` reais) —
 *   nunca 2 endereços principais ao mesmo tempo.
 * - Novo item ("Adicionar Endereço") nasce com `principal: false` — nunca
 *   demove o principal existente silenciosamente.
 *
 * Deliberadamente NÃO portado nesta etapa: autopreenchimento de
 * Endereço/Cidade/Bairro/Estado a partir do CEP (`getCorreiosCep`,
 * `Endereco.jsx` real, `onBlur` do campo CEP) — não fazia parte do escopo
 * desta etapa no plano de arquitetura (`docs/architecture/plano-aw2-1134-devedor.md`),
 * introduziria uma dependência nova (`services/correios/cep`) não prevista.
 * Débito técnico registrado, não um esquecimento.
 */
export default function EnderecoDevedor({
  enderecos, estadosOptions, podeModificarFase, isEditMode,
  onChangeEndereco, onAdicionarEndereco, onRemoverEndereco, onMarcarPrincipal,
}) {
  const desabilitado = !podeModificarFase || !isEditMode;

  return (
    <>
      <LinkAdicionar onClick={onAdicionarEndereco} disabled={desabilitado}>
        Adicionar Endereço +
      </LinkAdicionar>

      {enderecos.map((endereco, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={index} sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr) minmax(0, 1fr)', md: 'repeat(4, minmax(0, 1fr))' },
              gap: 'clamp(10px, 0.8vw, 22px)',
            }}
          >
            <Field
              label="CEP:"
              name="cep"
              value={endereco.cep}
              onChange={e => onChangeEndereco(index, 'cep', e.target.value)}
              disabled={desabilitado}
            />
            <Field
              label="Endereço:"
              name="endereco"
              value={endereco.endereco}
              onChange={e => onChangeEndereco(index, 'endereco', e.target.value)}
              disabled={desabilitado}
            />
            <Field
              label="Número:"
              name="numero"
              value={endereco.numero}
              onChange={e => onChangeEndereco(index, 'numero', e.target.value)}
              disabled={desabilitado}
            />
            <Field
              label="Complemento:"
              name="complemento"
              value={endereco.complemento}
              onChange={e => onChangeEndereco(index, 'complemento', e.target.value)}
              disabled={desabilitado}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr) minmax(0, 1fr)', md: 'minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 0.6fr) minmax(0, 1fr)' },
              gap: 'clamp(10px, 0.8vw, 22px)',
              alignItems: 'end',
              mt: 'clamp(10px, 0.8vw, 22px)',
            }}
          >
            <Field
              label="Bairro:"
              name="bairro"
              value={endereco.bairro}
              onChange={e => onChangeEndereco(index, 'bairro', e.target.value)}
              disabled={desabilitado}
            />
            <Field
              label="Cidade:"
              name="cidade"
              value={endereco.cidade}
              onChange={e => onChangeEndereco(index, 'cidade', e.target.value)}
              disabled={desabilitado}
            />
            <Field
              label="Estado:"
              tipo="select"
              name="estado"
              options={estadosOptions}
              value={endereco.estado}
              onChange={e => onChangeEndereco(index, 'estado', e.target.value)}
              disabled={desabilitado}
            />
            <Box>
              <FieldLabel>&nbsp;</FieldLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={Boolean(endereco.principal)}
                      onChange={() => onMarcarPrincipal(index)}
                      disabled={desabilitado}
                    />
                  )}
                  label={<Typography variant="body2">Principal</Typography>}
                  sx={{ ml: 0 }}
                />
                {index !== 0 && (
                  <Tooltip title="Excluir Endereço">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Excluir Endereço"
                        onClick={() => onRemoverEndereco(index)}
                        disabled={desabilitado}
                        sx={{ color: C.navy }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      ))}
    </>
  );
}

EnderecoDevedor.propTypes = {
  enderecos: PropTypes.arrayOf(PropTypes.shape({
    idDevedorEndereco: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    idEndereco: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    estado: PropTypes.string,
    endereco: PropTypes.string,
    numero: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    complemento: PropTypes.string,
    bairro: PropTypes.string,
    cidade: PropTypes.string,
    cep: PropTypes.string,
    principal: PropTypes.bool,
  })),
  estadosOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    value: PropTypes.string,
  })),
  podeModificarFase: PropTypes.bool,
  isEditMode: PropTypes.bool,
  onChangeEndereco: PropTypes.func,
  onAdicionarEndereco: PropTypes.func,
  onRemoverEndereco: PropTypes.func,
  onMarcarPrincipal: PropTypes.func,
};

EnderecoDevedor.defaultProps = {
  enderecos: [],
  estadosOptions: [],
  podeModificarFase: false,
  isEditMode: false,
  onChangeEndereco: () => null,
  onAdicionarEndereco: () => null,
  onRemoverEndereco: () => null,
  onMarcarPrincipal: () => null,
};
